import { useCallback } from 'react';
import { SMSTask } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV3, SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

const TASK_DELETE_EVENT = 'task_event_deleted';

const updateTasksArray = (existingTasks: SMSTask[], newTask: SMSTask, action: 'UPDATE' | 'DELETE') => {
  if (action === 'DELETE') {
    return existingTasks.filter((task) => task.taskId !== newTask.taskId);
  }

  const existingTask = existingTasks.find((task) => task.taskId === newTask.taskId);
  if (existingTask) {
    return existingTasks.map((task) => (task.taskId === newTask.taskId ? newTask : task));
  } else {
    return [...existingTasks, newTask];
  }
};

export const useSMSTaskWSHandler = () => {
  const { updateSMS } = SMSDataV3._QueryUpdaters.useQueryUpdaters();
  const smsV4QueryUpdaters = SMSDataV4._QueryUpdaters.useQueryUpdaters();
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const getUnifiedThreadEnabled = SMSDataSharedUtils.useGetUnifiedThreadEnabled();

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.SMS_TASK>>(
    async (event) => {
      const { params } = event;
      const action = 'action' in params && params.action === TASK_DELETE_EVENT ? 'DELETE' : 'UPDATE';
      if (getUnifiedThreadEnabled) {
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId: params.task.threadId,
            locationId: params.task.locationId,
            threadItemId: params.task.smsId,
          },
          updater: (threadItem) => {
            if (threadItem && 'smsMessage' in threadItem) {
              const sms = threadItem.smsMessage;
              return {
                smsMessage: {
                  ...sms,
                  tasks: updateTasksArray(sms.tasks ?? [], params.task, action),
                },
              };
            }
            return threadItem;
          },
          isOptimistic: false,
          getThreadOnly: true,
        });
        unifiedThreadUpdater.updateUnifiedThreadsTasksList({
          threadId: params.task.threadId,
          locationId: params.task.locationId,
          task: params.task,
          action,
        });
      } else {
        updateSMS({
          matchValues: {
            id: params.task.smsId,
            locationId: params.task.locationId,
          },
          newValues: (oldSms) => ({
            ...oldSms,
            tasks: updateTasksArray(oldSms.tasks ?? [], params.task, action),
          }),
        });
        smsV4QueryUpdaters.updateThreadsTasksList({
          threadId: params.task.threadId,
          locationId: params.task.locationId,
          task: params.task,
          action,
        });
        smsV4QueryUpdaters.upsertSms({
          matchValues: {
            smsId: params.task.smsId,
            threadId: params.task.threadId,
            locationId: params.task.locationId,
            createdAt: params.task.smsCreatedAt,
          },
          updater: (oldSms) => {
            if (!oldSms) return undefined;

            return {
              ...oldSms,
              tasks: updateTasksArray(oldSms.tasks ?? [], params.task, action),
            };
          },
          isOptimistic: false,
          updateOnly: true,
        });
      }
    },
    [updateSMS, smsV4QueryUpdaters]
  );

  return wsEventHandler;
};

export const useSMSTaskWSSubscription = () => {
  const handler = useSMSTaskWSHandler();
  return useWebsocketEventSubscription(NotificationType.SMS_TASK, handler);
};
